import React, { forwardRef, useCallback, useMemo, useState } from "react";
import {
    StyleSheet,
    View,
    ViewStyle,
    StyleProp,
    FlexAlignType,
    Pressable,
    Platform,
    ViewProps,
    GestureResponderEvent,
    ActivityIndicator,
    PixelRatio,
    LayoutChangeEvent,
} from "react-native";
import { useTheme } from "../hooks/use-theme";
import Swipeable from "react-native-gesture-handler/ReanimatedSwipeable";
import { LiftTheme } from "../theme";
import MarkdownView from "../components/MarkdownView";
import UserAvatar from "../screens/user/UserAvatar";
import Label, { LabelMode } from "./Label";
import Gap from "./Gap";
import Icon, { IconName } from "../components/Icon";
import Loading from "../components/Loading";
import Text from "../components/Text";
import { useAsyncError } from "../hooks/application";
import NotificationsCounter from "../components/NotificationsCounter";
import { useI18n } from "../hooks/i18n-hooks";
import { getTextLineCount } from "../utils/measure-text";

const FONT_SCALE = PixelRatio.getFontScale();

type IconSize = "small" | "big";

const SIZES: Record<IconSize, { iconSize: number; unreadSize: number }> = {
    small: {
        iconSize: 24,
        unreadSize: 8,
    },
    big: {
        iconSize: 48,
        unreadSize: 16,
    },
};

type RightIconMode =
    | "navigate"
    | "action"
    | "action_simple"
    | "empty"
    | "none"
    | "medium"
    | "redCta";

const RightActionDisplay: Record<
    RightIconMode,
    (theme: LiftTheme) => { iconColor: string; style?: StyleProp<ViewStyle>; size?: number }
> = {
    action: ({ colors }) => ({
        iconColor: colors.onSecondary,
        style: { backgroundColor: colors.secondary },
    }),
    navigate: ({ colors }) => ({ iconColor: colors.onSurfaceDisabled, style: undefined }),
    action_simple: ({ colors }) => ({
        iconColor: colors.secondary,
        style: { backgroundColor: "transparent" },
    }),
    empty: () => ({ iconColor: "transparent", style: undefined }),
    none: () => ({ iconColor: "transparent", style: undefined, size: 0 }),
    medium: ({ colors }) => ({ iconColor: colors.primary, style: undefined, size: 17 }),
    redCta: ({ colors }) => ({ iconColor: colors.error, style: undefined, size: 17 }),
};

type ListItemPressableProps = {
    disabled?: boolean;
    onPress: (e?: GestureResponderEvent) => void | Promise<void>;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    children?: React.ReactNode;
};
// eslint-disable-next-line react/display-name
const ListItemPressable = forwardRef<View, ListItemPressableProps>(
    ({ disabled, testID, style, onPress, children }, ref) => {
        const [pressing, setPressing] = useState(false);
        const throwError = useAsyncError();
        const onPressFunc = useCallback(
            (e: GestureResponderEvent) => {
                const _func = async () => {
                    setPressing(true);
                    try {
                        await onPress(e);
                    } catch (e) {
                        throwError(e as Error);
                    } finally {
                        setPressing(false);
                    }
                };
                void _func();
            },
            [onPress, throwError],
        );

        return (
            <Pressable
                ref={ref}
                disabled={disabled || pressing}
                testID={testID}
                id={testID}
                style={style}
                onPress={onPressFunc}
            >
                {children}
            </Pressable>
        );
    },
);

export type ListItemBorderType = "simple" | "selected" | "none";

type LabelType = {
    text?: string;
    mode?: LabelMode;
    onPress?: () => Awaited<void>;
};

export type ListItemProps = {
    title?: string;
    onLinkPress?: (url: string) => boolean;
    icon?: IconName | "__empty__";
    iconComponent?: React.ReactNode;
    avatarPath?: string | null;
    canEnlargeAvatar?: boolean;
    isAvatarLoading?: boolean;
    useBigAvatarOrIcon?: boolean;
    checked?: boolean | "light";
    selectable?: boolean;
    onPress?: () => void | Promise<void>;
    style?: StyleProp<ViewStyle>;
    swipeableStyle?: StyleProp<ViewStyle>;
    testID?: string;
    isShaded?: boolean;
    description?: string;
    maxDescriptionLines?: number;
    labels?: LabelType[];
    tags?: (string | React.ReactNode | null)[];
    badge?: React.ReactNode;
    inGroup?: boolean;
    iconColor?: string;
    globalTextColor?: string;
    onDelete?: () => void;
    disabled?: boolean;
    unread?: boolean | number; // display an indicator when true only relevant with an avatar
    border?: ListItemBorderType;
    roundedTop?: boolean;
    roundedBottom?: boolean;
    roundedVertical?: boolean;
    rightActionMode?: RightIconMode;
    rightIcon?: IconName;
    rightIconColor?: string;
    rightIconBackgroundColor?: string;
    onLayout?: ViewProps["onLayout"];
    loading?: boolean;
};

type TextBadgeProps = { text: string };
const TextBadge = ({ text }: TextBadgeProps) => <MarkdownView markdown={text} />;

// eslint-disable-next-line react/display-name
const ListItem = forwardRef<View, ListItemProps>(
    (
        {
            title,
            onLinkPress,
            description,
            maxDescriptionLines,
            labels,
            tags,
            badge,
            checked,
            selectable = false,
            onPress,
            style,
            swipeableStyle,
            testID,
            isShaded,
            icon,
            iconComponent,
            iconColor,
            globalTextColor,
            avatarPath,
            canEnlargeAvatar,
            isAvatarLoading = false,
            useBigAvatarOrIcon = false,
            inGroup = false,
            onDelete,
            disabled,
            unread,
            border,
            roundedTop,
            roundedBottom,
            roundedVertical,
            rightActionMode = "navigate",
            rightIconColor,
            rightIconBackgroundColor,
            rightIcon,
            onLayout,
            loading = false,
        },
        ref,
    ) => {
        const styles = useListItemStyle();
        const theme = useTheme();
        const i18n = useI18n();
        const { colors } = theme;

        const renderRightActions = React.useCallback(() => {
            return (
                <View style={styles.rightItem}>
                    <Icon name="trash" color="white" />
                </View>
            );
        }, [styles]);

        const sizing = useBigAvatarOrIcon ? SIZES["big"] : SIZES["small"];
        const textColor = useMemo(() => {
            if (globalTextColor) {
                return globalTextColor;
            }
            if (selectable) {
                if (checked === true) {
                    return colors.onPrimary;
                }
                return colors.body;
            }
        }, [selectable, checked, colors.body, colors.onPrimary, globalTextColor]);

        const titleStyle = [styles.title, { color: textColor }, disabled && styles.disabled];

        const iconSize = sizing.iconSize * FONT_SCALE;
        const unreadStyle = unread ? { borderWidth: 0, borderColor: colors.secondary } : {};
        const backgroundColor = useMemo(() => {
            if (selectable) {
                if (checked === true) {
                    return colors.primary;
                }
            }
        }, [selectable, checked, colors]);
        const [trucatedDescription, setTrucatedDescription] = useState<string>();
        const [isDescriptionExtended, setIsDescriptionExtended] = useState(false);

        const seeMoreLabel = useMemo(() => {
            if (trucatedDescription) {
                return {
                    text: !isDescriptionExtended ? i18n.t("seeMore") : i18n.t("seeLess"),
                    mode: "miniCta" as LabelMode,
                };
            }
        }, [i18n, isDescriptionExtended, trucatedDescription]);

        const onDescriptionLayout = useCallback(
            (e: LayoutChangeEvent) => {
                onLayout?.(e);
                // Binary search to find which text can fit within `maxDescriptionLines` prop
                if (description && maxDescriptionLines && !trucatedDescription) {
                    void (async () => {
                        const descriptionWidth = e.nativeEvent.layout.width;
                        let start = 0;
                        let end = description.length;
                        let bestFit = description;

                        const isLongerThanMaxDescriptionLines =
                            (await getTextLineCount(
                                description,
                                descriptionWidth,
                                styles.descriptionText,
                            )) > maxDescriptionLines;

                        while (isLongerThanMaxDescriptionLines && start <= end) {
                            const mid = Math.floor((start + end) / 2);
                            const testText = `${description.slice(0, mid)}...`;

                            const lineCount = await getTextLineCount(
                                testText,
                                descriptionWidth,
                                styles.descriptionText,
                            );

                            if (lineCount > maxDescriptionLines) {
                                end = mid - 1; // Réduire la longueur du texte
                            } else {
                                bestFit = testText;
                                start = mid + 1; // Essayer un texte plus long
                            }
                        }

                        if (isLongerThanMaxDescriptionLines) {
                            setTrucatedDescription(bestFit);
                        }
                    })();
                }
            },
            [
                description,
                maxDescriptionLines,
                onLayout,
                styles.descriptionText,
                trucatedDescription,
            ],
        );

        const onCustomLabelPress = useCallback(() => {
            setIsDescriptionExtended(!isDescriptionExtended);
        }, [isDescriptionExtended]);

        const content = (
            <View style={{ flex: 1, flexDirection: "row" }}>
                {icon ? (
                    <Icon
                        size={iconSize}
                        style={[styles.iconBase, styles.leftIcon, unreadStyle]}
                        color={
                            disabled
                                ? colors.backdrop
                                : checked === true
                                  ? colors.onPrimary
                                  : iconColor || colors.primary
                        }
                        name={icon}
                    />
                ) : iconComponent ? (
                    <View
                        style={[
                            styles.customIcon,
                            { width: iconSize, height: iconSize, borderRadius: iconSize / 2 },
                            unreadStyle,
                        ]}
                    >
                        {iconComponent}
                    </View>
                ) : avatarPath !== undefined ? (
                    <View style={styles.leftAvatarView}>
                        <UserAvatar
                            avatarPath={avatarPath}
                            size={iconSize}
                            style={[
                                styles.leftAvatar,
                                unreadStyle,
                                disabled || isAvatarLoading ? { opacity: 0.5 } : {},
                            ]}
                            canEnlarge={canEnlargeAvatar}
                        />
                        {isAvatarLoading && <Loading color="white" style={styles.avatarLoading} />}
                        {typeof unread === "number" && <NotificationsCounter count={unread} />}
                    </View>
                ) : null}
                {unread === true && (
                    <View
                        style={[
                            styles.unreadView,
                            {
                                width: sizing.unreadSize,
                                height: sizing.unreadSize,
                                borderRadius: sizing.unreadSize / 2,
                                borderWidth: sizing.unreadSize / 8,
                            },
                        ]}
                    />
                )}
                <View style={styles.innerView}>
                    <View style={styles.leftPart}>
                        {title && (
                            <>
                                {!onLinkPress ? (
                                    <Text style={titleStyle}>{title}</Text>
                                ) : (
                                    <MarkdownView
                                        markdownStyle={{ paragraph: titleStyle, link: styles.link }}
                                        markdown={title}
                                        onLinkPress={onLinkPress}
                                        onLayout={onLayout}
                                    />
                                )}
                            </>
                        )}
                        {description && (
                            <View style={styles.description}>
                                <MarkdownView
                                    markdown={`${(!isDescriptionExtended ? trucatedDescription : undefined) || description}`}
                                    markdownStyle={{
                                        link: styles.descriptionLink,
                                        paragraph: [
                                            styles.descriptionText,
                                            disabled ? styles.disabled : { color: textColor },
                                        ],
                                        bold: [
                                            styles.descriptionTextBold,
                                            disabled ? styles.disabled : { color: textColor },
                                        ],
                                        strong: [
                                            styles.descriptionTextBold,
                                            disabled ? styles.disabled : { color: textColor },
                                        ],
                                        bullet_list: [
                                            styles.descriptionText,
                                            { lineHeight: 24 },
                                            disabled ? styles.disabled : { color: textColor },
                                        ],
                                        ordered_list: [
                                            styles.descriptionText,
                                            { lineHeight: 24 },
                                            disabled ? styles.disabled : { color: textColor },
                                        ],
                                    }}
                                    onLinkPress={onLinkPress}
                                    onLayout={onDescriptionLayout}
                                />
                            </View>
                        )}
                        {tags && (
                            <View style={styles.tags}>
                                {tags.reduce<React.ReactNode[]>((acc, tag, idx) => {
                                    if (tag) {
                                        if (typeof tag === "string") {
                                            acc.push(
                                                <Text
                                                    key={tag}
                                                    style={[
                                                        styles.tagText,
                                                        disabled
                                                            ? styles.disabled
                                                            : { color: textColor },
                                                    ]}
                                                >
                                                    {tag}
                                                </Text>,
                                            );
                                        } else {
                                            acc.push(tag);
                                        }
                                        idx < tags.length - 1 &&
                                            acc.push(
                                                <Text
                                                    key={`sep-${idx}`}
                                                    style={[
                                                        styles.tagSep,
                                                        disabled
                                                            ? styles.disabled
                                                            : { color: textColor },
                                                    ]}
                                                >
                                                    •
                                                </Text>,
                                            );
                                    }
                                    return acc;
                                }, [] as React.ReactNode[])}
                            </View>
                        )}
                        {!seeMoreLabel && (description || title) ? <Gap gap={0.25} /> : null}
                        {seeMoreLabel ? (
                            <Pressable onPress={onCustomLabelPress} style={styles.seeMore}>
                                <Label
                                    text={seeMoreLabel.text}
                                    mode={seeMoreLabel.mode}
                                    style={[
                                        styles.labelText,
                                        disabled && { opacity: 0.9 },
                                        { fontWeight: "bold" },
                                    ]}
                                />
                            </Pressable>
                        ) : null}
                        {labels && labels.length > 0 ? (
                            <View style={styles.labelsContainer}>
                                {labels
                                    ?.filter((label) => label.text)
                                    ?.map((label, index) => (
                                        <Pressable
                                            key={index}
                                            onPress={label.onPress}
                                            disabled={!label.onPress}
                                            style={styles.label}
                                        >
                                            <Label
                                                text={label.text || ""}
                                                mode={label.mode || "default"}
                                                multiline
                                                style={[
                                                    styles.labelText,
                                                    disabled && { opacity: 0.9 },
                                                ]}
                                            />
                                        </Pressable>
                                    ))}
                            </View>
                        ) : null}
                    </View>
                    {badge}
                </View>
                {onPress && rightActionMode !== "none" ? (
                    <>
                        <Gap gap={1 / 2} />
                        <View
                            style={[
                                styles.baseIconContainer,
                                selectable && styles.selectableIconContainer,
                                checked && styles.checkedIconContainer,
                                checked === "light" && styles.checkedLightIconContainer,
                            ]}
                        >
                            {loading ? (
                                <ActivityIndicator size="small" />
                            ) : rightIcon ? (
                                <Icon
                                    style={[
                                        styles.customRightIcon,
                                        RightActionDisplay[rightActionMode](theme).style,
                                        rightIconBackgroundColor
                                            ? { backgroundColor: rightIconBackgroundColor }
                                            : null,
                                    ]}
                                    color={
                                        rightIconColor
                                            ? rightIconColor
                                            : RightActionDisplay[rightActionMode](theme).iconColor
                                    }
                                    size={
                                        RightActionDisplay[rightActionMode](theme).size ||
                                        useBigAvatarOrIcon
                                            ? 20
                                            : 13
                                    }
                                    name={rightIcon}
                                />
                            ) : (
                                <Icon
                                    style={[
                                        styles.iconBase,
                                        styles.rightIcon,
                                        !selectable && styles.rightIconChevron,
                                        checked &&
                                            !selectable && {
                                                // special case for checked icon but not selectable
                                                paddingTop: 5,
                                            },
                                    ]}
                                    color={
                                        selectable
                                            ? checked === true
                                                ? colors.secondary
                                                : checked === "light"
                                                  ? colors.background
                                                  : colors.onSurfaceDisabled
                                            : colors.onSurfaceDisabled
                                    }
                                    size={selectable ? (checked ? 15 : 24) : 16}
                                    name={
                                        selectable
                                            ? checked
                                                ? "check-solid"
                                                : "uncheck-circle"
                                            : rightActionMode == "navigate"
                                              ? "chevron-right"
                                              : "__empty__"
                                    }
                                />
                            )}
                        </View>
                    </>
                ) : null}
            </View>
        );
        let listItem;

        const finalStyle = [
            styles.view,
            roundedTop || roundedVertical ? styles.roundedTop : null,
            roundedBottom || roundedVertical ? styles.roundedBottom : null,
            inGroup ? null : { borderBottomWidth: 1, borderBottomColor: colors.accentLighter },
            (isShaded || loading) && styles.shaded,
            border === "simple" ? { borderColor: colors.accentLighter, borderWidth: 1 } : null,
            border === "selected" ? { borderColor: colors.primary, borderWidth: 2 } : null,
            style,
            backgroundColor ? { backgroundColor: backgroundColor } : {},
            {
                flexShrink: 1,
                alignItems: "center" as FlexAlignType,
            },
        ];
        if (onPress) {
            listItem = (
                <ListItemPressable
                    ref={ref}
                    disabled={disabled || loading}
                    testID={testID}
                    style={finalStyle}
                    onPress={onPress}
                >
                    {content}
                </ListItemPressable>
            );
        } else {
            listItem = (
                <View testID={testID} style={finalStyle}>
                    {content}
                </View>
            );
        }
        if (onDelete) {
            listItem = (
                <Swipeable
                    containerStyle={[styles.swipeContainer, swipeableStyle]}
                    renderRightActions={renderRightActions}
                    onSwipeableOpen={onDelete}
                >
                    {listItem}
                </Swipeable>
            );
        }

        return listItem;
    },
);

const useListItemStyle = () => {
    const { colors, roundness, gridSize, marginSize, fonts } = useTheme();

    const labelsMargin = useMemo(() => gridSize / 3, [gridSize]);

    return StyleSheet.create({
        view: {
            flexDirection: "row",
            paddingHorizontal: marginSize,
            paddingVertical: gridSize / 2,
            alignItems: "flex-start",
        },
        roundedTop: {
            borderTopLeftRadius: roundness,
            borderTopRightRadius: roundness,
        },
        roundedBottom: {
            borderBottomLeftRadius: roundness,
            borderBottomRightRadius: roundness,
        },
        shaded: {
            opacity: 0.4,
        },
        swipeContainer: {
            alignSelf: "stretch",
            borderRadius: roundness,
        },
        innerView: {
            flexDirection: "row",
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "space-between",
            flex: 1,
        },
        leftPart: {
            flexDirection: "column",
            flex: 1,
        },
        title: {
            marginTop: 0,
            marginBottom: 0,
            ...fonts.bodyLarge,
        },
        link: {
            color: colors.primary,
            fontWeight: "600",
        },
        disabled: {
            marginTop: 0,
            paddingTop: 0,
            marginBottom: 0,
            paddingBottom: 0,
            color: colors.backdrop,
        },
        descriptionLink: {
            color: colors.primary,
        },
        description: {
            marginTop: 0,
        },
        descriptionText: {
            ...fonts.bodyMedium,
            paddingBottom: 0,
        },
        descriptionTextBold: {
            ...fonts.bodyLarge,
            paddingBottom: 0,
        },
        labelsContainer: {
            flexWrap: "wrap",
            flexDirection: "row",
            margin: -labelsMargin,
        },
        seeMore: {
            flexDirection: "row",
            margin: 0,
            marginBottom: gridSize / 4,
        },
        label: {
            flexDirection: "row",
            margin: labelsMargin,
        },
        baseIconContainer: {
            flexGrow: 0,
            minWidth: 24,
            alignItems: "flex-start",
            alignSelf: "center",
        },
        selectableIconContainer: {
            alignItems: "center",
            justifyContent: "center",
        },
        checkedIconContainer: {
            backgroundColor: colors.accentLighter,
            marginLeft: 5,
            width: 24,
            height: 24,
            borderRadius: 24 / 2,
        },
        checkedLightIconContainer: {
            backgroundColor: colors.secondary,
        },
        labelText: {},
        iconBase: {
            alignSelf: "center",
            marginVertical: 0,
            padding: 0,
        },
        customIcon: {
            alignSelf: "flex-start",
            alignItems: "center",
            justifyContent: "center",
            marginRight: gridSize / 2,
            marginTop: 0,
            overflow: "hidden",
        },
        leftIcon: {
            alignSelf: "flex-start",
            marginRight: gridSize / 2,
            marginTop: 0,
        },
        unreadView: {
            position: "absolute",
            left: 0,
            top: 0,
            backgroundColor: colors.secondary,
            borderColor: colors.background,
        },
        leftAvatarView: {
            alignSelf: "flex-start",
            marginRight: gridSize / 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        leftAvatar: {
            backgroundColor: colors.backdrop,
        },
        avatarLoading: {
            position: "absolute",
        },
        emptyLeftIcon: {
            width: gridSize,
            height: gridSize,
        },
        rightIcon: {
            padding: 0,
            justifyContent: "center",
        },
        customRightIcon: {
            marginHorizontal: 5,
            //padding: 4,
            overflow: "hidden",
            height: 24,
            width: 24,
            borderRadius: 24 / 2,
        },
        rightItem: {
            flex: 1,
            flexDirection: "row-reverse",
            backgroundColor: colors.notification,
            justifyContent: "flex-start",
            alignItems: "center",
            paddingEnd: gridSize,
        },
        rightIconChevron: {
            // marginRight: -6,
        },
        tags: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginTop: 2,
            marginBottom: gridSize / 4,
        },
        tagText: {
            opacity: 0.5,
            ...fonts.labelSmall,
        },
        tagSep: {
            width: 10,
            textAlign: "center",
            opacity: 0.5,
            ...(Platform.OS === "ios"
                ? {
                      lineHeight: 20,
                  }
                : {}),
        },
        background: {
            backgroundColor: colors.accentLighter,
        },
        selectedBackground: {
            backgroundColor: colors.primary,
        },
        text: {
            color: colors.primary,
        },
        selectedText: {
            color: colors.onPrimary,
        },
    });
};

export default Object.assign(ListItem, { TextBadge });

export type BuildDescriptionOptions = {
    separator?: string;
    isList?: boolean;
};
export const buildDescription = (
    description: Array<string | undefined | null>,
    options: BuildDescriptionOptions = {},
) => {
    const separator = options.separator || "\n\n";
    const isList = options.isList || false;
    let descriptionArray = description.filter((d) => d);
    if (isList) {
        descriptionArray = descriptionArray.map((d) => `- ${d}`);
    }
    return descriptionArray.length > 0 ? descriptionArray.join(separator) : undefined;
};
